// google-provider.js — Google Calendar v3 REST.
//
// Uses syncToken-based incremental sync (delta capability) so subsequent
// fetches return only changed events. On 410 Gone the syncToken is no
// longer valid and we fall back to a full window fetch; the orchestrator
// tolerates this transparently.
//
// Credentials live in the workspace vault under `oauth:google:<sub>` as
// { access_token, refresh_token, expires_at }. Refresh goes through the
// token-manager so concurrent 401s collapse onto one refresh call.
//
// REST endpoints used:
//   GET  /users/me/calendarList                       — listCalendars
//   GET  /calendars/{calendarId}/events?syncToken=…    — fetchEvents
//   POST /calendars/{calendarId}/events               — createEvent
//   PUT  /calendars/{calendarId}/events/{eventId}     — updateEvent (+ If-Match)
//   DELETE                                            — deleteEvent (+ If-Match)

import { ProviderRegistry } from '../provider-registry.js';
import { ERROR_CODES } from './calendar-provider.js';
import { OAuthCalendarProvider } from './oauth-provider.js';
import { CalendarTransport as Transport } from './transport.js';

const API_BASE = 'https://www.googleapis.com/calendar/v3';

export class GoogleProvider extends OAuthCalendarProvider {
  /** @param {any} [opts] */
  constructor(opts: any) {
    super({
      ...opts,
      id: 'google',
      capabilities: { etag: true, rrule: true, delta: true, ...(opts?.capabilities || {}) },
    });
    this._token = null;
    this._syncTokens = new Map(); // calendarId → syncToken
  }

  // ── listCalendars ──
  /** @returns {Promise<any[]>} */
  async listCalendars() {
    const resp = await Transport.request({
      url: `${API_BASE}/users/me/calendarList`,
      method: 'GET',
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return (data.items || []).map((c: any) => ({
      id: c.id,
      title: c.summaryOverride || c.summary,
      color: c.backgroundColor || null,
      writable: c.accessRole === 'owner' || c.accessRole === 'writer',
      defaultCalendar: !!c.primary,
    }));
  }

  // ── fetchEvents ──
  /**
   * @param {any} opts - { calendarId, startDate, endDate, syncToken? }
   * @returns {Promise<any>}
   */
  async fetchEvents({ calendarId, startDate, endDate, syncToken }: any): Promise<any> {
    // syncToken is incompatible with timeMin/timeMax — Google rejects the
    // combination with a 400. Prefer the stored syncToken; otherwise bound by
    // the requested window. Either result set can span multiple pages: Google
    // caps a page at maxResults and returns nextPageToken when more remain, so
    // we MUST follow it — a single page silently truncates large calendars and
    // never sees nextSyncToken (only the final page carries it).
    const tok = syncToken || this._syncTokens.get(calendarId);
    const events: any[] = [];
    let pageToken: string | null = null;
    let nextSyncToken: string | null = null;

    // Build request options (which validates/refreshes the token via a vault
    // read + AES-GCM decrypt) ONCE per fetch, not once per page — a paginated
    // calendar otherwise re-decrypts the same token on every page. A token that
    // expires mid-fetch is still covered by transport.ts's reactive 401 ->
    // refresh, which updates reqOpts.credentials.token in place.
    const reqOpts = await this._requestOpts();

    for (;;) {
      const params = new URLSearchParams({
        singleEvents: 'false', // master + RRULE; orchestrator expands locally
        maxResults: '250',
        showDeleted: 'true',
      });
      // Pages 2+ carry ONLY pageToken (Google forbids pairing it with
      // syncToken/timeMin/timeMax); the other query params stay constant.
      if (pageToken) {
        params.set('pageToken', pageToken);
      } else if (tok) {
        params.set('syncToken', tok);
      } else {
        if (startDate) params.set('timeMin', new Date(startDate).toISOString());
        if (endDate) params.set('timeMax', new Date(endDate).toISOString());
      }
      const url = `${API_BASE}/calendars/${encodeURIComponent(calendarId)}/events?${params}`;
      let resp: any;
      try {
        resp = await Transport.request({ url, method: 'GET', ...reqOpts });
      } catch (e: any) {
        // 410 → syncToken invalid; drop it and re-fetch from scratch.
        if (e?.status === 410) {
          this._syncTokens.delete(calendarId);
          return this.fetchEvents({ calendarId, startDate, endDate });
        }
        throw e;
      }
      const data = await resp.json();
      for (const item of data.items || []) events.push(this._normalise(item, calendarId));
      if (data.nextSyncToken) nextSyncToken = data.nextSyncToken;
      pageToken = data.nextPageToken || null;
      if (!pageToken) break;
    }

    const deletedIds = events.filter((e: any) => e.deleted).map((e: any) => e.providerId);
    if (nextSyncToken) this._syncTokens.set(calendarId, nextSyncToken);
    return {
      events: events.filter((e: any) => !e.deleted),
      nextSyncToken,
      deletedIds,
    };
  }

  /**
   * @param {any} item - a Google Calendar event resource
   * @param {string} calendarId
   * @returns {any} the normalised event.
   */
  _normalise(item: any, calendarId: any) {
    const isAllDay = !!item.start?.date;
    const startIso = isAllDay ? `${item.start.date}T00:00:00.000Z` : item.start?.dateTime || null;
    const endIso = isAllDay ? `${item.end.date}T00:00:00.000Z` : item.end?.dateTime || null;
    return {
      providerId: item.id,
      accountId: this.accountId,
      calendarId,
      title: item.summary || '',
      description: item.description || '',
      location: item.location || '',
      startDate: startIso,
      endDate: endIso,
      allDay: isAllDay,
      rrule:
        (item.recurrence || []).find((r: any) => r.startsWith('RRULE:'))?.slice('RRULE:'.length) ||
        null,
      recurrenceId: item.recurringEventId
        ? (() => {
            const os =
              item.originalStartTime?.dateTime ||
              item.originalStartTime?.date ||
              item.start?.dateTime ||
              item.start?.date;
            const d = os ? new Date(os) : null;
            return d && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
          })()
        : null,
      masterProviderId: item.recurringEventId || null,
      etag: item.etag,
      sequence: item.sequence || 0,
      lastModified: item.updated,
      calendarTitle: null, // filled by listCalendars callers
      deleted: item.status === 'cancelled',
    };
  }

  // ── Writes ──
  /**
   * @param {any} event - a normalised event
   * @returns {any} a Google Calendar event resource.
   */
  _toGoogleEvent(event: any) {
    const out: any = {};
    if (event.title != null) out.summary = event.title;
    if (event.description != null) out.description = event.description;
    if (event.location != null) out.location = event.location;
    if (event.allDay) {
      out.start = { date: _yyyymmdd(event.startDate) };
      out.end = { date: _yyyymmdd(event.endDate) };
    } else {
      out.start = { dateTime: new Date(event.startDate).toISOString() };
      out.end = { dateTime: new Date(event.endDate).toISOString() };
    }
    if (event.rrule) out.recurrence = [`RRULE:${event.rrule}`];
    return out;
  }

  /**
   * @param {string} calendarId
   * @param {any} event
   * @returns {Promise<any>}
   */
  async createEvent(calendarId: any, event: any) {
    const resp = await Transport.request({
      url: `${API_BASE}/calendars/${encodeURIComponent(calendarId)}/events`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this._toGoogleEvent(event)),
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return {
      providerId: data.id,
      etag: data.etag,
      sequence: data.sequence || 0,
      lastModified: data.updated,
    };
  }

  /**
   * @param {string} calendarId
   * @param {string} providerId
   * @param {any} event
   * @param {any} [opts]
   * @returns {Promise<any>}
   */
  async updateEvent(calendarId: any, providerId: any, event: any, { etag } = {} as any) {
    const headers = { 'Content-Type': 'application/json' };
    if (etag) (headers as any)['If-Match'] = etag;
    const resp = await Transport.request({
      url: `${API_BASE}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(providerId)}`,
      method: 'PUT',
      headers,
      body: JSON.stringify(this._toGoogleEvent(event)),
      ...(await this._requestOpts()),
    });
    const data = await resp.json();
    return { etag: data.etag, sequence: data.sequence || 0, lastModified: data.updated };
  }

  /**
   * @param {string} calendarId
   * @param {string} providerId
   * @param {any} [opts]
   * @returns {Promise<void>}
   */
  async deleteEvent(calendarId: any, providerId: any, { etag } = {} as any) {
    const headers = {};
    if (etag) (headers as any)['If-Match'] = etag;
    try {
      await Transport.request({
        url: `${API_BASE}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(providerId)}`,
        method: 'DELETE',
        headers,
        ...(await this._requestOpts()),
      });
    } catch (e: any) {
      if (e?.code === ERROR_CODES.NOT_FOUND) return;
      throw e;
    }
  }
}

/**
 * @param {string} iso
 * @returns {string} YYYY-MM-DD (UTC).
 */
function _yyyymmdd(iso: any) {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// Phase 4.7: explicit ESM registration (import-driven ordering — see
// caldav-provider.js).
ProviderRegistry.register('google', GoogleProvider);
